import { Component, OnInit } from '@angular/core';
import { Filter } from '../../shared/services/api/table';
import { UserView } from '../../shared/data/data.generated';
import { ApiService } from '../../shared/services/api.service';
import { Router } from '@angular/router';
import {
  ColumnDefinitionV3,
  TableServiceV3,
  TextFilterV3Component,
  PageHeaderModule,
  SearchBarV2Module,
  TableV3Component,
  PageContentLayoutComponent,
  ButtonV3Component,
  ColumnRendererComponentV3,
  SelectFilterV3Component,
  SelectFilterRendererV3,
} from '@gea/digital-ui-lib';
import { FormBuilder } from '@angular/forms';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import {
  ActiveChipFilters,
  Chip,
  HrtFilterChipsComponent,
} from 'src/app/shared/components/hrt-filter-chips/hrt-filter-chips.component';
import { ApplicationFeature } from 'src/app/shared/data/application-feature';
import { UserActionsRenderer } from './user-renderers/actions-icons-renderer.component';
import { UserRoleRenderer } from './user-renderers/user-role-renderer.component';
import { NotificationsIconsRenderer } from './user-renderers/notifications-icons-renderer.component';
import { UtilityService } from 'src/app/shared/services/utility.service';
import { UserLastLoginRenderer } from './user-renderers/last-login-renderer.component';
import { PermissionsIconsRenderer } from './user-renderers/permission-icons-renderer.component';

@Component({
  selector: 'app-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss'],
  standalone: true,
  imports: [
    PageHeaderModule,
    SearchBarV2Module,
    HrtFilterChipsComponent,
    TableV3Component,
    PageContentLayoutComponent,
    ButtonV3Component,
  ],
})
export class UsersComponent implements OnInit {
  constructor(
    private readonly api: ApiService,
    private router: Router,
    private tableService: TableServiceV3,
    private fb: FormBuilder,
    private utilityService: UtilityService,
  ) {}

  public columnDefinitions: ColumnDefinitionV3[] = [
    {
      displayName: 'Name',
      key: 'Name',
      sortable: true,
      minWidth: 200,
      filter: {
        component: TextFilterV3Component,
      },
    },
    {
      displayName: 'Email',
      key: 'Email',
      sortable: true,
      minWidth: 200,
      filter: {
        component: TextFilterV3Component,
      },
    },
    {
      displayName: 'Role',
      key: 'RoleId',
      sortable: true,
      renderer: {
        component: UserRoleRenderer as ColumnRendererComponentV3<any>,
      },
      filter: {
        component: SelectFilterV3Component,
        config: {
          options: [
            { name: 'Guest', value: 0 },
            { name: 'Customer', value: 1000 },
            { name: 'Prefered Customer', value: 2000 },
            { name: 'External Sales', value: 3000 },
            { name: 'Internal Sales', value: 4000 },
            { name: 'Internal Support', value: 5000 },
            { name: 'Internal Admin', value: 6000 },
            { name: 'Dev Admin', value: 7000 },
          ],
        },
      } as unknown as SelectFilterRendererV3,
    },
    {
      displayName: 'Order Status Filters',
      key: 'OrderStatusFilters',
      sortable: true,
      filter: {
        component: TextFilterV3Component,
      },
    },
    {
      displayName: 'Notifications',
      key: 'no_key_notifications',
      sortable: false,
      renderer: {
        component: NotificationsIconsRenderer as ColumnRendererComponentV3<any>,
      },
    },
    {
      displayName: 'Permissions',
      key: 'no_key_permissions',
      sortable: false,
      minWidth: 300,
      renderer: {
        component: PermissionsIconsRenderer as ColumnRendererComponentV3<any>,
      },
    },
    {
      displayName: 'Last Login',
      key: 'LastModified',
      sortable: true,
      renderer: {
        component: UserLastLoginRenderer as ColumnRendererComponentV3<any>,
      },
    },
    {
      displayName: 'Actions',
      key: 'no_key_actions',
      sortable: false,
      renderer: {
        component: UserActionsRenderer as ColumnRendererComponentV3<any>,
        config: { refreshUsersTable: () => this.fetchUsersList() },
      },
    },
  ];

  tableId: string = 'hrtc-users-table';
  searchPlaceholder = 'Search Users';
  destroy$ = new Subject<void>();
  searchInput = new Subject<string>();
  count: number = 0;
  sortKey?: string;
  sortOrder?: string;
  statusFilters: string[] = [];
  page: number = 0;
  pageSize: number = 10;
  userSearch?: string;
  userResponse: UserView[] = [];
  isTableLoading: boolean = true;
  filters: Filter = {};
  addUserButtonDisabled: boolean = true;
  activeChipFilters: ActiveChipFilters = { searchText: '', filters: {} };
  searchForm = this.fb.group({
    search: [''],
  });

  ngOnInit(): void {
    this.handleHeaderClick();
    this.handleSearchChanged();
    this.addUserButtonDisabled = !this.api.CanEditFeature(
      ApplicationFeature.Users,
    );
  }

  private handleHeaderClick() {
    this.tableService
      .getFilterTableSettings(this.tableId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((settings) => {
        if (!settings?.columns) return;
        if (settings?.columns) {
          this.filters = this.buildBackendFiltersFromColumns(
            settings.columns,
            this.columnDefinitions,
          );
        }
        if (settings?.page !== undefined) {
          this.page = settings.page;
        }
        this.pageSize = settings?.pageSize ?? this.pageSize;
        const sortedColumn = Object.keys(settings.columns).find(
          (key) => settings.columns[key].sort ?? '',
        );

        if (sortedColumn) {
          this.sortKey = sortedColumn;
          if (settings.columns[sortedColumn].sort) {
            this.sortOrder =
              settings.columns[sortedColumn].sort === 'desc' ? 'True' : 'False';
          } else {
            this.sortOrder = '';
          }
        }

        this.activeChipFilters.filters = settings?.columns
          ? settings.columns
          : this.activeChipFilters.filters;

        this.fetchUsersList();
      });
  }

  private handleSearchChanged(): void {
    this.searchInput.pipe(debounceTime(500)).subscribe((searchTerm: string) => {
      if (searchTerm.length >= 3 || searchTerm.length == 0) {
        this.userSearch = searchTerm;
        this.utilityService.setTableSearchSetting(
          this.tableId,
          this.userSearch,
        );
        this.page = 0; // Reset to first page on new table search
        this.fetchUsersList();
      }
    });
  }

  searchValueChanged(newValue: string): void {
    this.searchInput.next(newValue);
  }

  fetchUsersList(): void {
    this.isTableLoading = true;
    if (this.utilityService.getTableSearchSetting(this.tableId)) {
      this.userSearch = this.utilityService.getTableSearchSetting(this.tableId);
    }
    const listOptions = {
      Search: this.userSearch ?? '',
      SortColumn: this.sortKey ?? '',
      SortDesc: this.sortOrder ?? '',
      PageSize: this.pageSize,
      Page: this.page,
      HasPaging: true,
      Filters: this.filters || {},
    };
    this.api.Users.GetUsers(listOptions).subscribe({
      next: (response: UserListResponse) => {
        this.userResponse = response.Data ?? [];
        this.activeChipFilters.searchText = this.userSearch ?? '';
        this.count = response.Count;
      },
      error: (error) => {
        console.error(error);
      },
      complete: () => {
        this.isTableLoading = false;
      },
    });
  }

  onChipRemoved(chip: Chip) {
    if (chip && chip.chipKey === 'clearAll') {
      // Clear all filters
      this.activeChipFilters = { searchText: '', filters: {} };
      this.userSearch = '';
      this.sortKey = '';
      this.sortOrder = '';
      this.filters = {};
      this.utilityService.setTableSearchSetting(this.tableId, '');
      this.searchForm.get('search')?.setValue('');
      this.tableService.setFilterTableSettings(this.tableId, {
        columns: this.activeChipFilters.filters,
        page: this.page,
        pageSize: this.pageSize,
      });
    } else if (
      chip &&
      chip.chipKey === 'searchText' &&
      chip.chipValue !== '' &&
      this.activeChipFilters.searchText === chip.chipValue
    ) {
      // Clear user search
      this.userSearch = '';
      this.utilityService.setTableSearchSetting(this.tableId, '');
      this.activeChipFilters.searchText = '';
      this.tableService.setFilterTableSettings(this.tableId, {
        columns: this.activeChipFilters.filters,
        page: this.page,
        pageSize: this.pageSize,
      });
      this.searchForm.get('search')?.setValue('');
    } else {
      if (chip && chip.chipKey !== 'searchText' && chip.chipValue !== '') {
        // 1. If chipValue is 'asc' or 'desc', remove "sort chip" and update documents
        if (chip.chipValue === 'asc' || chip.chipValue === 'desc') {
          if (this.activeChipFilters.filters[chip.chipKey]) {
            this.activeChipFilters.filters[chip.chipKey].sort = '';
            this.sortKey = '';
            this.sortOrder = '';
            this.tableService.setFilterTableSettings(this.tableId, {
              columns: this.activeChipFilters.filters,
              page: this.page,
              pageSize: this.pageSize,
            });
            this.fetchUsersList();
          }
        } else {
          // 2. Remove "filter chip" for chipKey with chipValue
          if (
            this.activeChipFilters.filters[chip.chipKey] &&
            Array.isArray(this.activeChipFilters.filters[chip.chipKey].filter)
          ) {
            this.activeChipFilters.filters[chip.chipKey].filter =
              this.activeChipFilters.filters[chip.chipKey].filter.filter(
                (val: any) => val !== chip.chipValue,
              );
            // Rebuild backend filters and update documents
            this.filters = this.buildBackendFiltersFromColumns(
              this.activeChipFilters.filters,
              this.columnDefinitions,
            );
            this.tableService.setFilterTableSettings(this.tableId, {
              columns: this.activeChipFilters.filters,
              page: this.page,
              pageSize: this.pageSize,
            });
            this.fetchUsersList();
          }
        }
      }
    }
  }

  onRowClicked(user: UserView) {
    this.router.navigate(['/user-editor', { isNew: false, id: user.Id }]);
  }

  onAddNewUserClicked() {
    this.router.navigate(['/user-editor', { isNew: true }]);
  }

  buildBackendFiltersFromColumns(columns: any, columnDefinitions: any[]): any {
    const backendFilters: any = {};

    columnDefinitions.forEach((colDef) => {
      const key = colDef.key;
      const col = columns[key];
      if (
        col &&
        col.filter &&
        Array.isArray(col.filter) &&
        col.filter.length > 0
      ) {
        // Find options for this column if available
        const options = colDef.filter?.config?.options || [];
        const values = col.filter.map((val: any) => {
          // Try to find a matching option for key/label mapping
          const found = options.find(
            (opt: any) =>
              opt.value?.toString() === val?.toString() ||
              opt.key?.toString() === val?.toString() ||
              opt.label === val ||
              opt.name === val,
          );
          return found
            ? {
                key: found.key ?? found.value ?? val,
                label: found.label ?? found.name ?? val,
              }
            : { key: val, label: val };
        });

        backendFilters[key] = {
          isClientSide: false,
          type: 'multiSelect',
          values,
        };
      }
    });

    return backendFilters;
  }

  ngOnDestroy(): void {
    this.tableService.updateFilterTableSettings(this.tableId, {
      columns: this.activeChipFilters.filters,
    });
    this.destroy$.complete();
    this.searchInput.complete();
  }
}

export interface UserListResponse {
  Count: number;
  Data: UserView[];
}
